import { useCallback, useMemo, useEffect, useState } from 'react';
import { MoneyInput } from './money-input';
import { FormInputCommonProps } from './types';
import { Listbox } from '@headlessui/react';
import { isArray } from 'lodash';
import SelectArrow from '../../images/select-arrow.svg';
import { classNames } from '../../utils';
import { CheckboxInput } from './checkbox-input';
import { RadioGroupInput } from './radio-group-input';
import { twMerge } from 'tailwind-merge';
import { get, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { LabelTooltip } from './input-row';

interface ButtonRenderPropArg {
  open: boolean;
  disabled: boolean;
  value: any;
}

export interface SelectOption {
  label: React.ReactNode | string;
  selectedLabel?: React.ReactNode;
  value: number | string;
  disabled?: boolean;
}

interface DropdownSelectInputProps extends FormInputCommonProps {
  multiple?: boolean;
  value?: number | number[] | string | string[] | null | any[];
  options: SelectOption[];
  onChange: (
    value: number | number[] | string | string[] | undefined | null
  ) => void;
  label: React.ReactNode;
  includeCheckboxes?: boolean;
  labelVariant?: 'plain' | 'pill';
  selectionVariant?: 'checkbox' | 'radio';
  changeOnClose?: boolean;
  hideSelection?: boolean;
  tooltipText?: string;
  tooltipClassName?: string;
  iconClassName?: string;
  placeholder?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  labelClassName?: string;
  labelWrapperClassName?: string;
  openWrapperClassName?: string;
  dropdownHeader?: React.ReactNode;
  selectArrowIcon?: React.ReactNode;
  buttonClassName?: string;
  showBorder?: boolean;
}

function getLabelForSelectOption(option?: SelectOption) {
  if (!option) return;

  return option.selectedLabel ?? option.label;
}

export const DropdownOptions: React.FC<{
  open: boolean;
  options: SelectOption[];
  disabled?: boolean;
  includeCheckboxes?: boolean;
  selectionVariant?: 'checkbox' | 'radio';
  dropdownClassName?: string;
  dropdownHeader: React.ReactNode;
}> = ({
  open,
  options,
  disabled,
  includeCheckboxes,
  selectionVariant,
  dropdownClassName,
  dropdownHeader = null,
}) => {
  return (
    <Listbox.Options>
      <div
        className={classNames(
          'absolute z-[9999] mt-[6px] flex max-h-[286px] w-auto flex-col gap-[6px] overflow-auto rounded-[4px] bg-white p-[8px] shadow-md md:w-[236px]',
          dropdownClassName
        )}
      >
        {dropdownHeader}
        {options.map((option) => {
          return (
            <Listbox.Option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={({ active, selected, disabled }) =>
                classNames(
                  `typo-body-small text-left text-sidewalk-900 rounded rounded-sm hover:bg-hardwood-100 flex h-[40px] w-full items-center p-[8px]`,
                  selected ? 'bg-hardwood-50' : 'bg-white',
                  active ? 'bg-hardwood-100' : '',
                  disabled ? 'cursor-default opacity-40' : 'cursor-pointer'
                )
              }
            >
              {/* @ts-ignore */}
              {({ active, selected, disabled, ...rest }) => {
                return (
                  <>
                    {includeCheckboxes ? (
                      selectionVariant === 'radio' ? (
                        <RadioGroupInput
                          readOnly
                          disabled={disabled}
                          checked={selected}
                          name={`${name}-${option.value}`}
                          className={'mr-2'}
                          value={option.value as string}
                        />
                      ) : (
                        <CheckboxInput
                          readOnly
                          disabled={disabled}
                          checked={selected}
                          name={`${name}-${option.value}`}
                          className={'mr-2'}
                        />
                      )
                    ) : null}
                    {option.label}
                  </>
                );
              }}
            </Listbox.Option>
          );
        })}
      </div>
    </Listbox.Options>
  );
};

const WatchOpenStatus: React.FC<{
  open?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, onClose, children }) => {
  const [hasBeenOpen, setHasBeenOpen] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setHasBeenOpen(true);
    } else {
      if (hasBeenOpen) {
        onClose();
      }
    }
  }, [open, hasBeenOpen, setHasBeenOpen]);
  return <>{children}</>;
};

export const DropdownSelectInput: React.FC<DropdownSelectInputProps> = ({
  name,
  multiple,
  value,
  changeOnClose,
  onChange,
  options,
  label,
  includeCheckboxes = true,
  labelVariant,
  selectionVariant,
  disabled,
  className,
  tooltipText,
  tooltipClassName,
  iconClassName,
  hideSelection,
  placeholder,
  inputClassName,
  dropdownClassName,
  labelClassName,
  labelWrapperClassName,
  openWrapperClassName,
  dropdownHeader,
  selectArrowIcon,
  buttonClassName,
  showBorder = true,
}) => {
  const methods = useFormContext();
  const errors = methods?.formState.errors;
  const error = methods && name ? get(errors, name) : null;

  const [localValue, setLocalValue] = useState<
    number | number[] | string | string[] | undefined | null
  >(value);

  const labelMapping: Record<number, SelectOption> = useMemo(() => {
    return options.reduce((acc, option) => {
      let { value, label } = option;
      return { ...acc, [value]: option };
    }, {});
  }, [options]);

  const getLabelDisplay = useCallback(
    (bag: ButtonRenderPropArg) => {
      let selectedLabel;

      if (isArray(bag.value)) {
        selectedLabel = bag.value
          .map((value) => {
            const selectedOption = labelMapping[value];
            return getLabelForSelectOption(selectedOption);
          })
          .join(', ');
      } else {
        selectedLabel = getLabelForSelectOption(labelMapping[bag.value]);
      }

      if (labelVariant === 'pill') {
        return (
          <div
            className={twMerge(
              'typo-body-small bg-hardwood-100 text-hardwood-700 hover:bg-hardwood-200 active:bg-hardwood-300 relative flex h-[28px] w-auto items-center overflow-hidden text-ellipsis rounded-[39px] pl-3 pr-[20px] text-left md:pl-[8px]',
              disabled
                ? 'bg-hardwood-50 pointer-events-none cursor-default'
                : '',
              hideSelection ? '' : 'lg:w-[250px]',
              labelWrapperClassName ? labelWrapperClassName : '',
              bag.open && openWrapperClassName ? openWrapperClassName : ''
            )}
          >
            <div className="flex items-center">
              {tooltipText ? (
                <LabelTooltip
                  tooltipClassName={tooltipClassName}
                  iconClassName={iconClassName}
                >
                  {tooltipText}
                </LabelTooltip>
              ) : null}
              <p
                className={classNames(
                  'mb-0 truncate',
                  hideSelection ? '' : 'lg:w-[225px]'
                )}
              >
                {hideSelection ? (
                  <span
                    className={classNames('mr-3 font-bold', labelClassName)}
                  >
                    {label}
                  </span>
                ) : (
                  <>
                    <span
                      className={classNames(
                        'font-bold mr-3 md:mr-0',
                        labelClassName
                      )}
                    >
                      {label}:
                    </span>
                    <span className={classNames('hidden md:inline')}>
                      {selectedLabel}
                    </span>
                  </>
                )}
              </p>
            </div>
            {selectArrowIcon ? (
              <div
                className={classNames(
                  'absolute right-[6px] top-[14px] translate-y-[-50%]  transition-all',
                  bag.open ? 'rotate-180' : ''
                )}
              >
                {selectArrowIcon}
              </div>
            ) : (
              <img
                alt="select-arrow"
                className={classNames(
                  'absolute right-[10px] top-[50%] h-[6px] w-[10px] translate-y-[-50%]  transition-all',
                  bag.open ? 'rotate-180' : ''
                )}
                src={SelectArrow}
              />
            )}
          </div>
        );
      }

      return (
        <div
          className={twMerge(
            'typo-body text-left relative flex h-[40px] min-w-[126px] items-center rounded-[4px] pl-[12px] pr-[32px]  outline-none ring-0  focus:border-none focus:shadow-none focus:ring-0 overflow-hidden whitespace-nowrap text-ellipsis',
            selectedLabel ? 'text-sidewalk-900' : 'text-sidewalk-500',
            error
              ? 'border-[1px] border-hydrant-500 bg-hydrant-30'
              : showBorder
                ? 'border-[1px] border-hardwood-300 bg-white'
                : 'bg-white',
            disabled ? 'bg-sidewalk-30 pointer-events-none cursor-default' : '',
            inputClassName ? inputClassName : ''
          )}
        >
          <span className="truncate">
            {placeholder && !selectedLabel
              ? placeholder
              : hideSelection
                ? ''
                : (selectedLabel ?? label)}
          </span>

          {selectArrowIcon ? (
            <div
              className={classNames(
                'absolute right-[4px] top-[13px] translate-y-[-50%]  transition-all',
                bag.open ? 'rotate-180' : ''
              )}
            >
              {selectArrowIcon}
            </div>
          ) : (
            <img
              alt="select-arrow"
              className={classNames(
                'absolute right-[10px] top-[50%] h-[6px] w-[10px] translate-y-[-50%]  transition-all',
                bag.open ? 'rotate-180' : ''
              )}
              src={SelectArrow}
            />
          )}
        </div>
      );
    },
    [label, value, error, disabled, labelVariant, labelMapping]
  );

  const onClose = useCallback(() => {
    if (changeOnClose && value !== localValue) {
      onChange(localValue);
    }
  }, [localValue, onChange]);

  useEffect(() => {
    setLocalValue(value);
  }, [value, setLocalValue]);

  return (
    <div className={twMerge('block w-full', className)}>
      <Listbox
        disabled={disabled}
        value={localValue ?? []}
        onChange={changeOnClose ? setLocalValue : onChange}
        multiple={multiple}
      >
        {/* @ts-ignore */}
        {({ open }) => {
          return (
            <WatchOpenStatus onClose={onClose} open={open}>
              <Listbox.Button className={twMerge('w-full', buttonClassName)}>
                {/* @ts-ignore */}
                {(bag) => getLabelDisplay(bag)}
              </Listbox.Button>
              <DropdownOptions
                open={open}
                includeCheckboxes={includeCheckboxes}
                options={options}
                disabled={disabled}
                selectionVariant={selectionVariant}
                dropdownClassName={dropdownClassName}
                dropdownHeader={dropdownHeader}
              />
            </WatchOpenStatus>
          );
        }}
      </Listbox>
      {name && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => {
            return (
              <div className="typo-body-small text-hydrant-500 absolute">
                {message}
              </div>
            );
          }}
        />
      ) : null}
    </div>
  );
};
